<template>
  <div>
    <v-expansion-panels v-model="panelAbierto" focusable class="rounded-xl">

      <!-- ══ TAB: CONFIGURACIÓN EMAIL ══════════════════════════════ -->
      <v-expansion-panel>
        <v-expansion-panel-header class="font-weight-bold">
          <div>
            <v-icon color="primary" class="mr-2">mdi-email-cog</v-icon>
            Integración de Correo Electrónico (SMTP)
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row class="mt-2">
            <v-col cols="12">
              <v-switch
                v-model="ajustes.smtp_activo"
                label="Activar envío de emails"
                color="primary"
                hint="Requiere configurar el servidor SMTP"
                persistent-hint
                inset
              />
            </v-col>

            <v-expand-transition>
              <v-col cols="12" v-if="ajustes.smtp_activo">
                <v-row>
                  <v-col cols="12" sm="8">
                    <v-text-field
                      v-model="ajustes.smtp_host"
                      label="Servidor SMTP"
                      outlined prepend-inner-icon="mdi-server"
                      placeholder="smtp.hostinger.com"
                    />
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model.number="ajustes.smtp_port"
                      label="Puerto"
                      outlined type="number"
                      prepend-inner-icon="mdi-numeric"
                      placeholder="465"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="ajustes.smtp_usuario"
                      label="Correo remitente (usuario SMTP)"
                      outlined prepend-inner-icon="mdi-email"
                      type="email"
                      placeholder="no-reply@migimnasio.com"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="ajustes.smtp_password"
                      label="Contraseña SMTP"
                      outlined prepend-inner-icon="mdi-lock"
                      :type="mostrarPass ? 'text' : 'password'"
                      :append-icon="mostrarPass ? 'mdi-eye-off' : 'mdi-eye'"
                      @click:append="mostrarPass = !mostrarPass"
                      placeholder="Deja vacío para no cambiar"
                      hint="Solo escribe si quieres cambiar la contraseña"
                      persistent-hint
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="ajustes.smtp_nombre"
                      label='Nombre del remitente (ej: "Mi Gym")'
                      outlined prepend-inner-icon="mdi-label"
                    />
                  </v-col>
                </v-row>

                <!-- Opciones de notificaciones -->
                <v-divider class="my-3" />
                <div class="text-subtitle-2 font-weight-bold mb-2">¿Qué emails enviar automáticamente?</div>
                <v-row>
                  <v-col cols="12" sm="4">
                    <v-switch v-model="ajustes.email_bienvenida" label="📧 Bienvenida al matricular" color="success" inset dense />
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-switch v-model="ajustes.email_vencimiento" label="⚠️ Alerta de vencimiento" color="warning" inset dense />
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-switch v-model="ajustes.email_pago" label="✅ Recibo de pago" color="info" inset dense />
                  </v-col>
                </v-row>

                <v-slider
                  v-if="ajustes.email_vencimiento"
                  v-model="ajustes.email_dias_aviso"
                  label="Avisar con X días de anticipación"
                  :min="1" :max="30" thumb-label="always"
                  color="warning" class="mt-2"
                />

                <!-- Botón de prueba -->
                <v-btn
                  color="primary" outlined rounded class="mt-2"
                  :loading="probandoSMTP"
                  @click="probarSMTP"
                >
                  <v-icon left>mdi-send-check</v-icon>
                  Enviar correo de prueba
                </v-btn>
                <v-btn
                  v-if="ajustes.email_vencimiento"
                  color="warning" outlined rounded class="mt-2 ml-2"
                  :loading="enviandoAlertas"
                  @click="enviarAlertasVencimiento"
                >
                  <v-icon left>mdi-email-multiple</v-icon>
                  Enviar alertas de vencimiento ahora
                </v-btn>

                <v-alert v-if="mensajeSMTP" :type="tipoMensajeSMTP" dense text class="mt-3">
                  {{ mensajeSMTP }}
                </v-alert>
              </v-col>
            </v-expand-transition>
          </v-row>

          <!-- Instrucciones de Hostinger -->
          <v-alert type="info" text dense class="mt-3" v-if="ajustes.smtp_activo">
            <strong>En Hostinger:</strong> Ve a Correo → Crear cuenta → escribe por ejemplo <code>no-reply@dvarela.com</code>.
            Luego usa esa cuenta y su contraseña aquí. Servidor: <code>smtp.hostinger.com</code>, Puerto: <code>465</code>.
            Después de subir los archivos, ejecuta <code>composer install</code> en la carpeta <code>api/</code> via SSH de Hostinger.
          </v-alert>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- ══ TAB: 2FA ══════════════════════════════════════════════ -->
      <v-expansion-panel>
        <v-expansion-panel-header class="font-weight-bold">
          <div>
            <v-icon color="success" class="mr-2">mdi-shield-lock</v-icon>
            Autenticación de Dos Factores (2FA)
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div v-if="!mi2fa.activo">
            <v-alert type="info" text class="mb-4">
              El 2FA protege tu cuenta con un código temporal de tu celular. Úsalo con <strong>Google Authenticator</strong>, <strong>Microsoft Authenticator</strong> o <strong>Authy</strong>.
            </v-alert>

            <v-btn color="success" rounded elevation="3" @click="generar2FA" :loading="generandoQR">
              <v-icon left>mdi-qrcode</v-icon>
              Activar 2FA en mi cuenta
            </v-btn>
          </div>

          <!-- QR to scan -->
          <div v-if="qr2FA && !mi2fa.activo" class="mt-4 text-center">
            <v-card class="pa-4 d-inline-block rounded-xl elevation-6">
              <img :src="qr2FA" alt="QR 2FA" width="200" class="d-block mx-auto" />
              <div class="text-caption mt-2 grey--text text-center">Escanea con tu app autenticadora</div>
              <div class="text-caption font-weight-bold mt-1 text-center" style="font-family:monospace;">{{ secreto2FA }}</div>
            </v-card>
            <div class="mt-4">
              <v-text-field
                v-model="codigoConfirmar2FA"
                label="Escribe el código de 6 dígitos para confirmar"
                outlined rounded
                type="number" maxlength="6"
                prepend-inner-icon="mdi-numeric"
                style="max-width:320px; margin: 0 auto;"
              />
              <v-btn
                color="success" rounded class="mt-2"
                :loading="activando2FA"
                :disabled="!codigoConfirmar2FA || String(codigoConfirmar2FA).length < 6"
                @click="activar2FA"
              >
                <v-icon left>mdi-check</v-icon> Confirmar y activar 2FA
              </v-btn>
            </div>
          </div>

          <!-- 2FA ya activo -->
          <div v-if="mi2fa.activo" class="text-center">
            <v-icon color="success" size="72">mdi-shield-check</v-icon>
            <div class="text-h6 font-weight-bold success--text mt-2">2FA Activo en tu cuenta</div>
            <div class="text-body-2 grey--text mt-1 mb-4">
              Para ingresar al sistema necesitarás tu código de Google Authenticator
            </div>
            <v-btn color="error" outlined rounded @click="desactivar2FA" :loading="desactivando2FA">
              <v-icon left>mdi-shield-off</v-icon> Desactivar 2FA
            </v-btn>
          </div>

          <v-alert v-if="mensaje2FA" :type="tipoMensaje2FA" dense text class="mt-3">
            {{ mensaje2FA }}
          </v-alert>
        </v-expansion-panel-content>
      </v-expansion-panel>

    </v-expansion-panels>

    <!-- ══ PANEL: AVISOS MASIVOS ══════════════════════════════════ -->
    <v-card rounded="xl" class="mt-4 elevation-2">
      <v-card-title class="font-weight-bold">
        <v-icon color="deep-purple" class="mr-2">mdi-email-multiple</v-icon>
        Enviar Aviso Masivo a Miembros
      </v-card-title>
      <v-card-subtitle>
        Los correos se envían en segundo plano en lotes — sin riesgo de timeout.
      </v-card-subtitle>
      <v-divider />
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <v-switch
              v-model="aviso.soloActivos"
              label="Enviar solo a miembros ACTIVOS"
              color="deep-purple" inset dense
              @change="contarDestinatarios"
            />
          </v-col>
          <v-col cols="12" sm="6" class="d-flex align-center">
            <v-chip :color="aviso.totalDestinatarios > 0 ? 'deep-purple' : 'grey'" dark small>
              <v-icon left small>mdi-account-multiple</v-icon>
              {{ aviso.totalDestinatarios !== null
                ? aviso.totalDestinatarios + ' destinatario(s)'
                : '...' }}
            </v-chip>
            <v-btn x-small text color="deep-purple" @click="contarDestinatarios"
                   :loading="aviso.contando" class="ml-2">
              <v-icon left x-small>mdi-refresh</v-icon> Actualizar
            </v-btn>
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="aviso.asunto"
              label="Asunto del correo *"
              outlined dense
              prepend-inner-icon="mdi-format-title"
              placeholder="Ej: ¡Oferta especial de renovación para ti!"
              counter="100" maxlength="100"
              :disabled="aviso.enviando"
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="aviso.mensaje"
              label="Mensaje *"
              outlined dense
              prepend-inner-icon="mdi-text"
              placeholder="Escribe aquí el mensaje para tus miembros. Usa saltos de línea para separar párrafos."
              rows="6" counter="2000" maxlength="2000"
              hint="Cada salto de línea se convierte en párrafo en el correo."
              persistent-hint
              :disabled="aviso.enviando"
            />
          </v-col>
        </v-row>

        <!-- Progreso de envío -->
        <template v-if="aviso.enviando || aviso.progreso.total > 0">
          <v-divider class="my-3" />
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption text--secondary">
              ✅ {{ aviso.progreso.enviados }} enviados
              <template v-if="aviso.progreso.errores > 0">
                · ❌ {{ aviso.progreso.errores }} fallidos
              </template>
              · ⏳ {{ aviso.progreso.pendientes }} pendientes
            </span>
            <span class="text-caption font-weight-bold deep-purple--text">
              {{ aviso.progreso.porcentaje }}%
            </span>
          </div>
          <v-progress-linear
            :value="aviso.progreso.porcentaje"
            color="deep-purple" rounded height="8"
            :striped="aviso.enviando"
          />
          <p class="text-caption text--secondary mt-1">{{ aviso.progreso.mensaje }}</p>
        </template>

        <v-alert v-if="aviso.resultado && !aviso.enviando"
                 :type="aviso.tipo" dense text class="mt-3">
          {{ aviso.resultado }}
        </v-alert>
      </v-card-text>
      <v-card-actions class="pa-4 pt-0">
        <v-spacer />
        <v-btn
          color="deep-purple" dark rounded elevation="3"
          :loading="aviso.enviando"
          :disabled="!aviso.asunto || !aviso.mensaje || !aviso.totalDestinatarios"
          @click="enviarAvisoMasivo"
        >
          <v-icon left>mdi-send</v-icon>
          Enviar a {{ aviso.totalDestinatarios || '?' }} miembro(s)
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>

</template>

<script>
import HttpService from '../../Servicios/HttpService'

export default {
  name: 'ConfigEmailSeguridad',
  props: ['ajustes'],

  data: function() {
    return {
      panelAbierto: null,
      mostrarPass: false,
      // SMTP
      probandoSMTP: false,
      enviandoAlertas: false,
      mensajeSMTP: '',
      tipoMensajeSMTP: 'success',
      // 2FA
      mi2fa: { activo: false },
      qr2FA: '',
      secreto2FA: '',
      codigoConfirmar2FA: '',
      generandoQR: false,
      activando2FA: false,
      desactivando2FA: false,
      mensaje2FA: '',
      tipoMensaje2FA: 'info',
      // Avisos masivos
      aviso: {
        soloActivos: true,
        asunto: '',
        mensaje: '',
        totalDestinatarios: null,
        contando: false,
        enviando: false,
        resultado: '',
        tipo: 'success',
        progreso: {
          total: 0,
          enviados: 0,
          pendientes: 0,
          errores: 0,
          porcentaje: 0,
          mensaje: '',
        },
      },
    }
  },

  mounted: function() {
    this.verificarEstado2FA()
    this.contarDestinatarios()
  },

  methods: {
    probarSMTP: function() {
      var self = this
      this.probandoSMTP = true
      this.mensajeSMTP = ''
      HttpService.registrar({ metodo: 'probar_smtp', config: this.ajustes }, 'correos.php')
      .then(function(r) {
        self.probandoSMTP = false
        self.mensajeSMTP = r ? r.mensaje : 'Error de conexión'
        self.tipoMensajeSMTP = r && r.ok ? 'success' : 'error'
      })
    },

    enviarAlertasVencimiento: function() {
      var self = this
      this.enviandoAlertas = true
      HttpService.registrar({ metodo: 'enviar_alertas_vencimiento' }, 'correos.php')
      .then(function(r) {
        self.enviandoAlertas = false
        self.mensajeSMTP = r ? r.mensaje : 'Error'
        self.tipoMensajeSMTP = 'success'
      })
    },

    verificarEstado2FA: function() {
      var self = this
      HttpService.obtenerConDatos({ metodo: 'estado_2fa' }, 'auth.php')
      .then(function(r) {
        if (r) self.mi2fa = r
      }).catch(function() {})
    },

    generar2FA: function() {
      var self = this
      this.generandoQR = true
      HttpService.obtenerConDatos({ metodo: 'generar_2fa_qr' }, 'auth.php')
      .then(function(r) {
        self.generandoQR = false
        if (r) {
          self.qr2FA = r.qr_url
          self.secreto2FA = r.secreto
        }
      })
    },

    activar2FA: function() {
      var self = this
      this.activando2FA = true
      HttpService.obtenerConDatos({
        metodo: 'activar_2fa',
        codigo: String(this.codigoConfirmar2FA),
      }, 'auth.php').then(function(r) {
        self.activando2FA = false
        if (r && r.ok) {
          self.mi2fa.activo = true
          self.qr2FA = ''
          self.mensaje2FA = '✅ 2FA activado correctamente'
          self.tipoMensaje2FA = 'success'
        } else {
          self.mensaje2FA = (r && r.error) || 'Código incorrecto'
          self.tipoMensaje2FA = 'error'
        }
      })
    },

    desactivar2FA: function() {
      var self = this
      if (!confirm('¿Seguro que quieres desactivar el 2FA?')) return
      this.desactivando2FA = true
      HttpService.obtenerConDatos({ metodo: 'desactivar_2fa' }, 'auth.php')
      .then(function(r) {
        self.desactivando2FA = false
        if (r && r.ok) {
          self.mi2fa.activo = false
          self.mensaje2FA = 'El 2FA fue desactivado'
          self.tipoMensaje2FA = 'info'
        }
      })
    },

    contarDestinatarios: function() {
      var self = this
      this.aviso.contando = true
      HttpService.registrar({
        metodo: 'contar_destinatarios',
        soloActivos: this.aviso.soloActivos,
      }, 'correos.php').then(function(r) {
        self.aviso.contando = false
        self.aviso.totalDestinatarios = r ? r.total : 0
      }).catch(function() { self.aviso.contando = false })
    },

    enviarAvisoMasivo: function() {
      var self = this
      if (!this.aviso.asunto || !this.aviso.mensaje) return
      var dest = this.aviso.totalDestinatarios || 0
      if (!confirm('¿Enviar el aviso a ' + dest + ' miembro(s)? Esta acción no se puede deshacer.')) return

      this.aviso.enviando   = true
      this.aviso.resultado  = ''
      this.aviso.progreso   = { total: dest, enviados: 0, pendientes: dest, errores: 0, porcentaje: 0, mensaje: 'Encolando mensajes...' }

      // 1) Encolar todos los mensajes (respuesta inmediata)
      HttpService.registrar({
        metodo:      'enviar_aviso_masivo',
        asunto:      this.aviso.asunto,
        mensaje:     this.aviso.mensaje,
        soloActivos: this.aviso.soloActivos,
      }, 'correos.php').then(function(r) {
        if (!r || !r.ok) {
          self.aviso.enviando  = false
          self.aviso.resultado = r ? r.mensaje : 'Error al encolar'
          self.aviso.tipo      = 'error'
          return
        }
        self.aviso.progreso.total      = r.encolados || dest
        self.aviso.progreso.pendientes = r.encolados || dest
        self.aviso.progreso.mensaje    = r.mensaje
        // 2) Consumir la cola con polling cada 3 segundos
        self._procesarCola()
      }).catch(function() {
        self.aviso.enviando  = false
        self.aviso.resultado = 'Error de conexión'
        self.aviso.tipo      = 'error'
      })
    },

    _procesarCola: function() {
      var self = this
      HttpService.registrar({ metodo: 'procesar_cola', lote: 10 }, 'correos.php')
      .then(function(r) {
        if (!r) {
          self.aviso.enviando  = false
          self.aviso.resultado = 'Error procesando cola'
          self.aviso.tipo      = 'error'
          return
        }
        // Actualizar contadores acumulados
        self.aviso.progreso.enviados  += (r.enviados  || 0)
        self.aviso.progreso.errores   += (r.errores   || 0)
        self.aviso.progreso.pendientes = r.pendientes  || 0
        self.aviso.progreso.mensaje    = r.mensaje     || ''

        var total = self.aviso.progreso.total
        var hechos = self.aviso.progreso.enviados + self.aviso.progreso.errores
        self.aviso.progreso.porcentaje = total > 0 ? Math.round((hechos / total) * 100) : 100

        if (r.pendientes > 0) {
          // Seguir procesando en 2 segundos
          setTimeout(function() { self._procesarCola() }, 2000)
        } else {
          // Cola vacía — finalizar
          self.aviso.enviando   = false
          self.aviso.progreso.porcentaje = 100
          var env = self.aviso.progreso.enviados
          var err = self.aviso.progreso.errores
          self.aviso.tipo      = err > 0 && env === 0 ? 'error' : (err > 0 ? 'warning' : 'success')
          self.aviso.resultado = '✅ Envío completo: ' + env + ' enviados' + (err > 0 ? ', ' + err + ' fallidos.' : '.')
          // Limpiar formulario si todo OK
          if (env > 0) {
            self.aviso.asunto  = ''
            self.aviso.mensaje = ''
          }
        }
      }).catch(function() {
        // Reintentar automáticamente en caso de error de red
        setTimeout(function() { self._procesarCola() }, 5000)
      })
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/Configuracion/ConfigEmailSeguridad.vue